import React, { useState } from 'react';

import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { IconButton } from '@tmap/mmm-style-guide/src/IconButton';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import {
  Check,
  ExpandLess,
  ExpandMore,
} from '@tmap/mmm-style-guide/src/Icon';
import { Checkbox } from '@tmap/mmm-style-guide/src/Checkbox';
import { Collapse } from '@tmap/mmm-style-guide/src/Transition';
import { FormControlLabel } from '@tmap/mmm-style-guide/src/Form';

import StyledMenuItem from './styledMenuItem';

const Tile = styled(Box, {
  shouldForwardProp: (p) => p !== 'checked',
})(({ theme, checked }) => ({
  border: checked
    ? `1px solid ${theme.palette.primary.main}`
    : `1px solid ${theme.palette.text.secondary}`,
  backgroundColor: checked
    ? theme.palette.primary.veryLight
    : theme.palette.common.white,
  fontWeight: checked ? 600 : 400,
  padding: theme.spacing(2, 1.5),
  borderRadius: '8px',
  cursor: 'pointer',
  svg: {
    color: checked
      ? theme.palette.secondary.main
      : theme.palette.text.secondary,
  },
}));

const TextPrimaryIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

// prevent jitter when font weight changes by including an invisible bolded element
const FixedTypography = styled(Typography, {
  shouldForwardProp: (p) => p !== 'content',
})(({ content }) => ({
  '::before': {
    content: `"${content}"`,
  },
  '::after': {
    display: 'block',
    content: `"${content}"`,
    fontWeight: 500,
    height: '1px',
    color: 'transparent',
    overflow: 'hidden',
    visibility: 'hidden',
  },
}));

const getLabel = (option) => option?.title || option?.label;

export default function OptionCheckbox(props) {
  const {
    option,
    primaryValue,
    childValue,
    onToggle,
    mode,
    level = 0,
    variant = 'standard',
  } = props;
  const [expanded, setExpanded] = useState(false);
  if (option.hidden) return null;

  // nested rendering
  if (option.children) {
    const checked = primaryValue.includes(option.value)
      || option.children.every((child) => childValue.includes(child.value));
    const indeterminate = option.children.some((child) => childValue.includes(child.value));
    const checkIndicator = (
      <Checkbox
        color='secondary'
        offColor='text.secondary'
        checked={checked}
        indeterminate={!checked && indeterminate}
      />
    );
    if (mode === 'select') {
      return (
        <>
          <StyledMenuItem level={level} value={option.value} onClick={onToggle(option.value)}>
            {checkIndicator}
            <FixedTypography content={getLabel(option)} />
          </StyledMenuItem>
          {option.children.map((opt) => (
            <OptionCheckbox
              mode={mode}
              option={opt}
              primaryValue={childValue}
              onToggle={onToggle}
              level={level + 1}
              key={opt.value}
            />
          ))}
        </>
      );
    }
    return (
      <>
        <FormControlLabel
          label={getLabel(option)}
          onChange={onToggle(option.value)}
          control={checkIndicator}
        />
        <TextPrimaryIconButton size='small' onClick={() => setExpanded((bool) => !bool)} sx={{ marginLeft: -2 }}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </TextPrimaryIconButton>
        <Collapse in={expanded}>
          <Grid container mb={3} ml={3}>
            {option.children.map((opt) => (
              <Grid item xs={12} key={`option-${level + 1}-${opt.value}`}>
                <OptionCheckbox
                  option={opt}
                  primaryValue={childValue}
                  onToggle={onToggle}
                />
              </Grid>
            ))}
          </Grid>
        </Collapse>
      </>
    );
  }

  const checked = primaryValue.includes(option.value);
  const checkIndicator = (
    <Checkbox
      color='secondary'
      offColor='text.secondary'
      checked={checked}
    />
  );
  if (mode === 'select') {
    return (
      <StyledMenuItem
        value={option.value}
        onClick={onToggle(option.value)}
        level={level}
      >
        {checkIndicator}
        <FixedTypography content={getLabel(option)} />
      </StyledMenuItem>
    );
  }

  return (variant === 'tile'
    ? (
      <Tile
        checked={checked}
        onClick={onToggle(option.value)}
      >
        <Grid container columnSpacing={0.5}>
          <Grid item xs={12} sx={{ mb: 3 }} display='flex'>
            <option.icon fontSize='large' />
          </Grid>
          {checked && (
            <Grid item sx={{ height: 3 }}>
              <Check sx={{ color: (theme) => theme.palette.secondary.main }} fontSize='small' />
            </Grid>
          )}
          <Grid item xs>
            {getLabel(option)}
          </Grid>
        </Grid>
      </Tile>
    )
    : (
      <FormControlLabel
        label={getLabel(option)}
        onChange={onToggle(option.value)}
        control={checkIndicator}
      />
    )
  );
}
